import type { Character } from "../types/entity.js";
import type { AbilityName, SkillName } from "../types/entity.js";
import { SKILL_ABILITY, abilityModifier } from "../types/entity.js";
import { rollD20 } from "./dice.js";

/** Result returned by both rollSkillCheck and rollSave. */
export interface CheckResult {
  /** Raw d20 result (1–20). */
  readonly roll: number;
  /** Ability modifier + proficiency bonus (doubled if expertise). */
  readonly bonus: number;
  /** roll + bonus. */
  readonly total: number;
  /** true when total >= dc. No auto-success on 20 or auto-fail on 1 for checks/saves. */
  readonly success: boolean;
}

/**
 * Roll a skill check for a character against a DC.
 * Proficiency bonus is added when the character is proficient; doubled on expertise.
 */
export function rollSkillCheck(character: Character, skill: SkillName, dc: number): CheckResult {
  const ability = SKILL_ABILITY[skill];
  const abilMod = abilityModifier(character.abilities[ability]);

  let profBonus = 0;
  if (character.skillProficiencies.has(skill)) {
    profBonus = character.proficiencyBonus;
    if (character.expertiseSkills.has(skill)) {
      profBonus *= 2;
    }
  }

  const bonus = abilMod + profBonus;
  const roll = rollD20();
  const total = roll + bonus;
  return { roll, bonus, total, success: total >= dc };
}

/**
 * Roll a saving throw for a character against a DC.
 * Proficiency bonus is added when the character has save proficiency in the ability.
 */
export function rollSave(character: Character, ability: AbilityName, dc: number): CheckResult {
  const abilMod = abilityModifier(character.abilities[ability]);
  const profBonus = character.saveProficiencies.has(ability) ? character.proficiencyBonus : 0;

  const bonus = abilMod + profBonus;
  const roll = rollD20();
  const total = roll + bonus;
  return { roll, bonus, total, success: total >= dc };
}
